import { useEffect, useRef } from "react";
import axios from "axios";
import API_URL from "../config/config";
import {
  WEBSITE_NOTIFICATION_EVENT,
  requestWebsiteNotificationPermission,
  showWebsiteBrowserNotification,
} from "../utils/browserNotifications";

const ORDER_POLL_INTERVAL_MS = 10000;

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const getOrderKey = (order = {}) => String(order?._id || order?.orderId || "");

const getOrderRef = (order = {}) => order?.orderId || order?._id || "your order";

const hasRiderAssigned = (order = {}) =>
  Boolean(order?.rider?._id || order?.rider?.id || order?.rider);

const getOrderStatuses = (order = {}) =>
  Array.isArray(order?.orderStatus) ? order.orderStatus : [];

const getLastStatusTimestamp = (status = {}) =>
  Number(status?.timestamp || status?.createdAt || Date.now());

const buildStatusNotification = (order, statusEntry, index) => {
  const message = String(statusEntry?.message || "");
  const normalizedMessage = normalizeText(message);
  const orderRef = getOrderRef(order);
  const timestamp = getLastStatusTimestamp(statusEntry);
  const suffix = `order:${getOrderKey(order)}:status:${index}:${timestamp}`;

  if (normalizedMessage.includes("pickup started")) {
    return {
      eventKey: `${suffix}:pickup-started`,
      title: "Pickup Started",
      body: `Pickup has started for order #${orderRef}.`,
      tag: `pickup-started-${getOrderKey(order)}`,
    };
  }

  if (normalizedMessage.includes("arrived at pickup")) {
    return {
      eventKey: `${suffix}:arrived-pickup`,
      title: "Rider Arrived at Pickup",
      body: `Rider has arrived at the pickup point for order #${orderRef}.`,
      tag: `arrived-pickup-${getOrderKey(order)}`,
    };
  }

  if (normalizedMessage.includes("pickup closed")) {
    return {
      eventKey: `${suffix}:pickup-closed`,
      title: "Pickup Completed",
      body: `Parcel pickup is completed for order #${orderRef}.`,
      tag: `pickup-closed-${getOrderKey(order)}`,
    };
  }

  if (normalizedMessage.includes("drop started")) {
    return {
      eventKey: `${suffix}:drop-started`,
      title: "Drop Started",
      body: `Delivery is on the way for order #${orderRef}.`,
      tag: `drop-started-${getOrderKey(order)}`,
    };
  }

  if (normalizedMessage.includes("arrived at drop")) {
    return {
      eventKey: `${suffix}:arrived-drop`,
      title: "Rider Arrived at Drop",
      body: `Rider has reached the drop point for order #${orderRef}.`,
      tag: `arrived-drop-${getOrderKey(order)}`,
    };
  }

  if (normalizedMessage.includes("drop closed")) {
    return {
      eventKey: `${suffix}:drop-closed`,
      title: "Drop Completed",
      body: `A drop point has been completed for order #${orderRef}.`,
      tag: `drop-closed-${getOrderKey(order)}`,
    };
  }

  if (normalizedMessage.includes("order completed")) {
    return {
      eventKey: `${suffix}:delivered`,
      title: "Order Delivered",
      body: `Order #${orderRef} has been delivered successfully.`,
      tag: `delivered-${getOrderKey(order)}`,
    };
  }

  return null;
};

const buildOrderAssignedNotification = (order) => ({
  eventKey: `order:${getOrderKey(order)}:assigned:${
    order?.rider?._id || order?.rider?.id || "rider"
  }`,
  title: "Order Accepted",
  body: `A rider has accepted your order #${getOrderRef(order)}.`,
  tag: `assigned-${getOrderKey(order)}`,
});

const buildOrderWithdrawnNotification = (order) => ({
  eventKey: `order:${getOrderKey(order)}:withdrawn:${
    order?.updatedAt || order?.time_stamp || Date.now()
  }`,
  title: "Order Withdrawn",
  body: `The rider withdrew from order #${getOrderRef(
    order
  )}. We are assigning another rider.`,
  tag: `withdrawn-${getOrderKey(order)}`,
});

const buildCreatedOrderNotification = (order) => ({
  eventKey: `order:${getOrderKey(order)}:created`,
  title: "Order Created",
  body: `Your order #${getOrderRef(order)} has been created successfully.`,
  tag: `created-${getOrderKey(order)}`,
});

const buildDeliveredStatusFallback = (order) => ({
  eventKey: `order:${getOrderKey(order)}:delivered:${
    order?.updatedAt || order?.time_stamp || Date.now()
  }`,
  title: "Order Delivered",
  body: `Order #${getOrderRef(order)} has been delivered successfully.`,
  tag: `delivered-${getOrderKey(order)}`,
});

const buildCancelledStatusFallback = (order) => ({
  eventKey: `order:${getOrderKey(order)}:cancelled:${
    order?.cancelled_at || order?.updatedAt || Date.now()
  }`,
  title: "Order Cancelled",
  body: `Order #${getOrderRef(order)} has been cancelled.`,
  tag: `cancelled-${getOrderKey(order)}`,
});

const notifyFromPayload = (payload) => {
  showWebsiteBrowserNotification({
    ...payload,
    url: payload?.url || "/orders",
  });
};

const WebsiteNotificationManager = () => {
  const previousOrdersRef = useRef(new Map());
  const isPrimedRef = useRef(false);
  const pollingRef = useRef(null);

  useEffect(() => {
    requestWebsiteNotificationPermission().catch(() => {});
  }, []);

  useEffect(() => {
    const handleCustomNotification = (event) => {
      notifyFromPayload(event?.detail || {});
    };

    window.addEventListener(
      WEBSITE_NOTIFICATION_EVENT,
      handleCustomNotification
    );

    return () => {
      window.removeEventListener(
        WEBSITE_NOTIFICATION_EVENT,
        handleCustomNotification
      );
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const syncOrdersAndNotify = async () => {
      const token = window.localStorage.getItem("token");
      if (!token) {
        previousOrdersRef.current = new Map();
        isPrimedRef.current = false;
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/order/customer/orders`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
          params: {
            compact: true,
            page: 1,
            limit: 50,
          },
        });

        const nextOrders = response.data?.order || response.data?.orders || [];
        const nextMap = new Map(
          nextOrders
            .filter((order) => getOrderKey(order))
            .map((order) => [getOrderKey(order), order])
        );

        if (isPrimedRef.current && isMounted) {
          const previousMap = previousOrdersRef.current;

          nextMap.forEach((currentOrder, orderKey) => {
            const previousOrder = previousMap.get(orderKey);

            if (!previousOrder) {
              notifyFromPayload(buildCreatedOrderNotification(currentOrder));
              return;
            }

            const previousHadRider = hasRiderAssigned(previousOrder);
            const currentHasRider = hasRiderAssigned(currentOrder);

            if (!previousHadRider && currentHasRider) {
              notifyFromPayload(buildOrderAssignedNotification(currentOrder));
            }

            if (
              previousHadRider &&
              !currentHasRider &&
              normalizeText(currentOrder?.status) === "new" &&
              normalizeText(currentOrder?.withdrawal_type)
            ) {
              notifyFromPayload(buildOrderWithdrawnNotification(currentOrder));
            }

            const previousStatuses = getOrderStatuses(previousOrder);
            const currentStatuses = getOrderStatuses(currentOrder);

            if (currentStatuses.length > previousStatuses.length) {
              currentStatuses
                .slice(previousStatuses.length)
                .forEach((statusEntry, indexOffset) => {
                  const payload = buildStatusNotification(
                    currentOrder,
                    statusEntry,
                    previousStatuses.length + indexOffset
                  );
                  if (payload) {
                    notifyFromPayload(payload);
                  }
                });
            } else {
              const previousStatus = normalizeText(previousOrder?.status);
              const currentStatus = normalizeText(currentOrder?.status);

              if (
                previousStatus !== currentStatus &&
                currentStatus === "delivered"
              ) {
                notifyFromPayload(buildDeliveredStatusFallback(currentOrder));
              }

              if (
                previousStatus !== currentStatus &&
                currentStatus === "cancelled"
              ) {
                notifyFromPayload(buildCancelledStatusFallback(currentOrder));
              }
            }
          });
        }

        previousOrdersRef.current = nextMap;
        isPrimedRef.current = true;
      } catch (error) {
        if (error?.response?.status === 401) {
          previousOrdersRef.current = new Map();
          isPrimedRef.current = false;
        }
      }
    };

    syncOrdersAndNotify();
    pollingRef.current = window.setInterval(
      syncOrdersAndNotify,
      ORDER_POLL_INTERVAL_MS
    );

    const handleFocus = () => {
      syncOrdersAndNotify();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleFocus);

    return () => {
      isMounted = false;
      if (pollingRef.current) {
        window.clearInterval(pollingRef.current);
      }
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleFocus);
    };
  }, []);

  return null;
};

export default WebsiteNotificationManager;
